/* hs-eslint ignored failing-rules */

/* eslint-disable react-hooks/exhaustive-deps */
'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useMemo, useContext, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import I18n from 'I18n';
import getLang from 'I18n/utils/getLang';
import Raven from 'raven-js';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { OLAF } from 'HubStyleTokens/colors';
import H1 from 'UIComponents/elements/headings/H1';
import UILink from 'UIComponents/link/UILink';
import UIButton from 'UIComponents/button/UIButton';
import UIClickable from 'UIComponents/button/UIClickable';
import UIFlex from 'UIComponents/layout/UIFlex';
import { H5 } from 'UIComponents/elements/headings';
import CoreStorefrontHeader from 'marketplace-ui-common/storefront/components/StorefrontHeader';
import MediaQuery from 'marketplace-ui-common/layout/components/MediaQuery';
import InputHighlighter from 'ecosystem-marketplace-core/components/storefront/InputHighlighter';
import { TYPEAHEAD_SEARCH_MIN_CHARS } from 'ecosystem-marketplace-core/constants/search';
import * as Industries from 'ecosystem-marketplace-core/constants/solutions/routes/IndustryRoute';
import * as ScreenSizes from 'ecosystem-marketplace-core/constants/layout/ScreenSizes';
import { useScreenSize } from 'ecosystem-marketplace-core/hooks/useScreenSize';
import { useProfileSearchBarQuery } from 'ecosystem-marketplace-core/solutions/hooks/useProfileSearchBarQuery';
import { buildGetCachedSearchValues, buildSetCachedSearchValue } from 'ecosystem-marketplace-core/utils/cachedSearch';
import { debounce } from 'ecosystem-marketplace-core/utils/debounce';
import { useResponsiveProps } from 'ecosystem-marketplace-core/hooks/useResponsiveProps';
import SearchBar from 'marketplace-ui-common/search/components/SearchBar';
import { updateStorefrontFilters, storefrontFiltersInitialState, getMultiSelectStorefrontFilters } from '../slices/storefrontFiltersSlice';
import * as FilterKey from '../constants/search/FilterKey';
import { getSortOptionValue } from '../utils/filters';
import { RELEVANCE_DESC } from '../constants/search/SortField';
import StorefrontUsageTrackerContext from '../context/StorefrontUsageTrackerContext';
import { getIsResultsHeader } from '../slices/storefrontFiltersSlice';
import { getCanonicalAppContext, getIsPublicApp } from '../selectors/app';
import { getIsUngatedForHomepage } from '../selectors/auth';
import { push } from '../utils/history';
import { translateFilterValue } from '../utils/i18n';
import { getFullLocationUrl } from '../utils/urls';
import MobileStorefrontHeader from './MobileStorefrontHeader';
import { getServices } from '../slices/serviceCatalogSlice';
import HeroSection from './homepage/HeroSection';
import FindPartnerWizard from './homepage/questionnaire/FindPartnerWizard';
const HEADER_PATH = 'ecosystem-marketplace-solutions-ui.storefront.header';
const FILTERS_PATH = 'ecosystem-marketplace-solutions-ui.storefront.filters';
const CACHED_SEARCH_VALUES_KEY = 'ecosystem-marketplace-solutions:searches';
const getCachedSearchValues = buildGetCachedSearchValues(CACHED_SEARCH_VALUES_KEY);
const setCachedSearchValue = buildSetCachedSearchValue(CACHED_SEARCH_VALUES_KEY);

const getHeaderCustomStyle = isMobile => {
  return {
    MAIN_SECTION_CLASSNAME: isMobile ? 'p-top-7' : 'p-top-14',
    BACKGROUND_COLOR: '#00497D',
    GRADIENT_ANGLE: '193.97deg',
    START_GRADIENT_COLOR: '#00497D',
    END_GRADIENT_COLOR: '#0FBFBF'
  };
};

const getProfileOptionsGroup = (items, input) => {
  const filteredOptions = items.map(result => result.item ? {
    dropdownText: result.dropdownText,
    slug: result.item.slug,
    text: result.text,
    title: result.text,
    value: result.item.id
  } : {
    dropdownText: /*#__PURE__*/_jsx(UILink, {
      children: result.text
    }),
    text: result.text,
    value: input,
    viewAll: true
  });
  return {
    text: I18n.text(`${HEADER_PATH}.search.typeaheadResultsHeader`),
    options: filteredOptions
  };
};

const getIndustryOptionsGroup = input => {
  const filteredOptions = Object.keys(Industries).filter(industry => translateFilterValue(FilterKey.INDUSTRY, industry).toLowerCase().includes(input)).map(industry => {
    const translatedText = translateFilterValue(FilterKey.INDUSTRY, industry);
    return {
      dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
        text: translatedText,
        value: input
      }),
      filterKey: FilterKey.INDUSTRY,
      text: translatedText,
      value: industry
    };
  });
  return {
    text: I18n.text(`${FILTERS_PATH}.industry.label`),
    options: filteredOptions
  };
};

const StorefrontHeader = () => {
  const dispatch = useDispatch();
  const {
    trackInteraction
  } = useContext(StorefrontUsageTrackerContext);
  const canonicalAppContext = useSelector(getCanonicalAppContext);
  const filters = useSelector(getMultiSelectStorefrontFilters);
  const catalogServices = useSelector(getServices);
  const isResultsHeader = useSelector(getIsResultsHeader);
  const isUngatedForHomepage = useSelector(getIsUngatedForHomepage);
  let questionnaireCompleted;

  try {
    questionnaireCompleted = localStorage.getItem('QUESTIONNAIRE_COMPLETED');
  } catch (error) {
    console.error(error);
  }

  const {
    placeholder
  } = useResponsiveProps({
    placeholder: {
      xs: undefined,
      sm: I18n.text(`${HEADER_PATH}.search.placeholder`)
    }
  });
  const [isSticky, setIsSticky] = useState(false);
  const onStickyChange = useCallback(evt => {
    setIsSticky(evt.target.value);
  }, []);
  const [showFindPartnerWizard, setShowFindPartnerWizard] = useState(false);
  const cachedSearchValues = getCachedSearchValues();
  const recentSearchOptions = useMemo(() => {
    if (!cachedSearchValues.length) {
      return [];
    }

    return [{
      text: I18n.text(`${HEADER_PATH}.search.recent`),
      options: cachedSearchValues.map(value => ({
        text: value,
        value,
        cachedSearchValue: true
      }))
    }];
  }, [cachedSearchValues]);
  const isOnHomepage = useMemo(() => {
    const homepageRegExp = new RegExp(/marketplace\/solutions(\/)?$/);
    return homepageRegExp.test(window.location.pathname);
  }, [window.location.pathname]);
  const getServiceOptionsGroup = useCallback(input => {
    const filteredOptions = catalogServices.filter(service => service.name.toLowerCase().includes(input)).map(service => {
      return {
        dropdownText: /*#__PURE__*/_jsx(InputHighlighter, {
          text: service.name,
          value: input
        }),
        filterKey: FilterKey.SERVICE,
        text: service.name,
        value: service.id
      };
    });
    return {
      text: I18n.text(`${FILTERS_PATH}.service.label`),
      options: filteredOptions
    };
  }, [catalogServices]);
  const onSearchSubmit = useCallback(value => {
    const valuesFromCache = getCachedSearchValues();
    setCachedSearchValue(value, valuesFromCache);
    dispatch(updateStorefrontFilters({
      updates: Object.assign({}, storefrontFiltersInitialState, {
        [FilterKey.SEARCH]: value,
        [FilterKey.SORTS]: RELEVANCE_DESC
      })
    }));
    trackInteraction('submit search', {
      search: value,
      searchLength: value.length
    });
  }, [dispatch, trackInteraction]);
  const onClearSearch = useCallback(() => {
    if (filters[FilterKey.SEARCH]) {
      dispatch(updateStorefrontFilters({
        updates: {
          [FilterKey.SEARCH]: storefrontFiltersInitialState[FilterKey.SEARCH]
        }
      }));
      dispatch(updateStorefrontFilters({
        updates: {
          [FilterKey.SORTS]: storefrontFiltersInitialState[FilterKey.SORTS]
        }
      }));
    }
  }, [dispatch, filters]);
  const trackSelectedOptionInteraction = useCallback((itemType, value) => {
    trackInteraction('select search autofill item', {
      itemType,
      value
    });
  }, [trackInteraction]);
  const onSelectedOptionChange = useCallback(({
    target: {
      value: option
    }
  }) => {
    const {
      value,
      filterKey,
      cachedSearchValue,
      slug,
      viewAll
    } = option;

    if (viewAll) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('see all results', value);
      return;
    }

    if (filterKey) {
      dispatch(updateStorefrontFilters({
        updates: {
          [filterKey]: [value]
        }
      }));
      trackSelectedOptionInteraction(filterKey, value);
      return;
    }

    if (cachedSearchValue) {
      onSearchSubmit(value);
      trackSelectedOptionInteraction('recent search', value);
      return;
    }

    push(`/${slug}`);
    trackSelectedOptionInteraction('profile', slug);
  }, [dispatch, onSearchSubmit, trackSelectedOptionInteraction]);
  const isPublicApp = useSelector(getIsPublicApp);
  const {
    fetcher
  } = useProfileSearchBarQuery({
    appContext: isPublicApp ? 'public' : 'app',
    query: {
      sorts: getSortOptionValue(RELEVANCE_DESC),
      callingUserLanguage: getLang()
    }
  }, isUngatedForHomepage);

  const buildOptionsFromRecords = (recordData, input) => {
    const profileOptions = getProfileOptionsGroup(recordData, input);
    const serviceOptions = getServiceOptionsGroup(input);
    const industryOptions = getIndustryOptionsGroup(input);
    return [serviceOptions, profileOptions, industryOptions];
  };

  const loadOptions = useCallback(debounce((input, callback) => {
    if (input.trim().length < TYPEAHEAD_SEARCH_MIN_CHARS) {
      callback(null, {
        options: recentSearchOptions
      });
      return;
    }

    fetcher(input).then(resp => {
      const options = buildOptionsFromRecords(resp, input);
      callback(null, {
        options
      });
    }).catch(() => {
      Raven.captureMessage('Profile typeahead search failed', {
        level: 'error',
        extra: {
          search: input
        }
      });
      const options = buildOptionsFromRecords([], input);
      callback(null, {
        options
      });
    });
  }), [filters, recentSearchOptions]);
  const renderedTitle = useMemo(() => {
    const translatedText = /*#__PURE__*/_jsx(FormattedMessage, {
      message: `${HEADER_PATH}.title`
    });

    const headerElement = isResultsHeader ? /*#__PURE__*/_jsx("span", {
      className: "is--heading-1",
      children: translatedText
    }) : /*#__PURE__*/_jsx(H1, {
      "aria-level": 1,
      children: translatedText
    });
    return /*#__PURE__*/_jsx(UILink, {
      use: "unstyled",
      href: getFullLocationUrl({
        pathname: '/'
      }, canonicalAppContext),
      children: headerElement
    });
  }, [isResultsHeader, canonicalAppContext]);
  const screenSize = useScreenSize();
  const isMobileView = screenSize === ScreenSizes.XS || screenSize === ScreenSizes.SM;
  const searchInputSize = useMemo(() => {
    if (!isMobileView && !isSticky) {
      return 'xl';
    }

    return 'default';
  }, [isMobileView, isSticky]);
  const headerCustomStyle = useMemo(() => getHeaderCustomStyle(isMobileView), [isMobileView]);
  return /*#__PURE__*/_jsxs(Fragment, {
    children: [/*#__PURE__*/_jsx(CoreStorefrontHeader, {
      collapsedTitle: /*#__PURE__*/_jsx(UIClickable, {
        onClick: () => push('/'),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${HEADER_PATH}.collapsedTitle`
        })
      }),
      customStyle: headerCustomStyle,
      onStickyChange: onStickyChange,
      subtitle: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${HEADER_PATH}.subtitle`
      }),
      title: renderedTitle // only collapsedTitle will be used when ungated for homepage
      ,
      use: isUngatedForHomepage ? 'collapsed' : 'default',
      children: /*#__PURE__*/_jsx(MediaQuery, {
        minScreenSize: ScreenSizes.SM,
        alternate: /*#__PURE__*/_jsx(MobileStorefrontHeader, {
          customStyle: headerCustomStyle,
          isUngatedForHomepage: isUngatedForHomepage,
          onClearSearch: onClearSearch,
          onSearchSubmit: onSearchSubmit,
          loadOptions: loadOptions,
          onSelectedOptionChange: onSelectedOptionChange,
          placeholder: placeholder,
          searchTerm: filters[FilterKey.SEARCH]
        }),
        children: isUngatedForHomepage ? /*#__PURE__*/_jsxs(UIFlex, {
          justify: "between",
          children: [/*#__PURE__*/_jsx(MediaQuery, {
            minScreenSize: isOnHomepage ? ScreenSizes.SM : ScreenSizes.LG,
            children: /*#__PURE__*/_jsx(UIButton, {
              onClick: () => push('/all'),
              use: "unstyled",
              truncate: true,
              className: "p-top-3 m-x-4",
              children: /*#__PURE__*/_jsx(H5, {
                style: {
                  color: OLAF
                },
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `${HEADER_PATH}.browseAll`
                })
              })
            })
          }), /*#__PURE__*/_jsxs(UIFlex, {
            justify: "end",
            children: [!isOnHomepage && /*#__PURE__*/_jsx(MediaQuery, {
              minScreenSize: ScreenSizes.MD,
              children: /*#__PURE__*/_jsx(UIButton, {
                className: "m-right-2",
                use: "secondary-ghost",
                onClick: () => setShowFindPartnerWizard(true),
                children: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: `${HEADER_PATH}.findPartner`
                })
              })
            }), /*#__PURE__*/_jsx("div", {
              style: {
                flexGrow: 'inherit',
                maxWidth: 400
              },
              children: /*#__PURE__*/_jsx(SearchBar, {
                loadOptions: loadOptions,
                inputSize: 'default',
                inputValue: filters[FilterKey.SEARCH],
                onClear: onClearSearch,
                onSearchSubmit: onSearchSubmit,
                onSelectedOptionChange: onSelectedOptionChange,
                placeholder: placeholder,
                menuStyle: {
                  height: 'auto',
                  maxHeight: '400px'
                }
              })
            })]
          })]
        }) : /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(SearchBar, {
            loadOptions: loadOptions,
            inputSize: searchInputSize,
            inputValue: filters[FilterKey.SEARCH],
            onClear: onClearSearch,
            onSearchSubmit: onSearchSubmit,
            onSelectedOptionChange: onSelectedOptionChange,
            placeholder: placeholder
          })
        })
      })
    }), isOnHomepage && !questionnaireCompleted && isUngatedForHomepage && /*#__PURE__*/_jsx(HeroSection, {
      onShowFindPartnerWizard: () => setShowFindPartnerWizard(true),
      isMobileView: isMobileView
    }), showFindPartnerWizard && /*#__PURE__*/_jsx(FindPartnerWizard, {
      onClose: () => setShowFindPartnerWizard(false),
      isMobileView: isMobileView
    })]
  });
};

export default StorefrontHeader;